import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { Injectable, OnDestroy } from '@angular/core';

import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { SesionUsuario } from '../interfaces/SesionUsuario.interface';
import { AppState } from '../state/app.state';
import { selectorLoading } from '../state/selectors/login.selector';
import { loginSuccess } from '../state/actions/login.actions';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { MsalResponse } from '../interfaces/msalResponse.interface';
import { Apollo, gql } from 'apollo-angular';
import { ForkResponseMsal } from '../interfaces/forkResponse.interface';
import { rolesUsuario } from '../constants/rolesUsers.constant';

const _scopesAzure = environment.scopesAzure;
const _me = environment.meAzure;

@Injectable({
  providedIn: 'root',
})
export class MicrosoftAuthService implements OnDestroy {
  sesion: Observable<SesionUsuario> = new Observable();

  //#region SUBSCRIPTION
  private loginSubscription: Subscription = new Subscription();
  private tokenSilentSubscription: Subscription = new Subscription();

  private forkJoinSubscription: Subscription = new Subscription();

  private selectorLoadingSubscription: Subscription = new Subscription();

  private queryMunicipioSubscription: Subscription = new Subscription();
  private queryUnidadRespSubscription: Subscription = new Subscription();
  private queryUnidadAdmSubscription: Subscription = new Subscription();
  private queryPuestoSubscription: Subscription = new Subscription();
  //#endregion

  //#region GRAPHQL
  GET_MUNICIPIO = gql`
  query($idMunicipio: Int){
    ctlMunicipios (where:  {idMunicipio: {eq: $idMunicipio} } ) {
      idMunicipio
      municipio: nombre
    }
  }`;

  GET_UNIDAD_RESPONSABLE = gql`
  query($idUnidadResponsable: Int){
    ctlUnidadesResponsables (where:  { idUnidadResponsable: {eq: $idUnidadResponsable }  estatus:{eq:true} } ){
      idUnidadResponsable
      unidadResponsable: nombre
      siglas
    }
  }`;

  GET_UNIDAD_ADMINISTRATIVA = gql`
  query($idUnidadAdministrativa: Int){
    ctlUnidadesAdministrativas (where:  { idUnidadAdministrativa: {eq: $idUnidadAdministrativa }  estatus:{eq:true} } ){
      idUnidadAdministrativa
      unidadAdministrativa: nombre
      siglas
    }
  }`;

  GET_PUESTO = gql`
  query($idPuesto: Int){
    ctlPuestos (where:  { idPuesto: { eq: $idPuesto }  estatus:{ eq: true } } ){
      idPuesto
      puesto: nombre
    }
  }`;
  //#endregion

  constructor(
    private _authService: MsalService,
    private _router: Router,
    private _http: HttpClient,
    private _store: Store<AppState>,
    private _notificationService: NotificationService,
    private _apollo: Apollo
  ) { }

  // public handleRedirectPromise() {
  //   this._authService.instance.handleRedirectPromise().then((res: any) => {
  //     if (res != null && res.account != null) {
  //       this._authService.instance.setActiveAccount(res.account);
  //     }
  //   });
  // }
    public async handleRedirectPromise() {
    await this._authService.instance.initialize();
    this._authService.instance.handleRedirectPromise().then((res: any) => {
      if (res != null && res.account != null) {
        this._authService.instance.setActiveAccount(res.account);
      }
    });
  }

  public login() {
    if (!this.isLoggedIn()) {
      this.loginSubscription = this._authService.loginPopup().subscribe({
        next: (response: AuthenticationResult) => {
          this._authService.instance.setActiveAccount(response.account);

          //Token generado por el servicio de login, al hacer la llamada al active directory
          //Este token sirve para obtener la foto de perfil del usuario
          //Es necesario activar la lectura del perfil de usuario en el app.module

          this.getUser(response.accessToken);
        },
        error: (error: any) => {
          this._notificationService.error(
            'Autenticación cancelada por el usuario',
            'Autenticación'
          );
        }
      }
      );
    } else {
      this.getUser();
    }
  }

  private isLoggedIn(): boolean {
    const active = this._authService.instance.getActiveAccount() != null;
    return active;
  }

  private getTokenSilent() {
    return this._authService
      .acquireTokenSilent({
        scopes: [_scopesAzure],
      })
      .pipe(map((res: any) => res.accessToken));
  }

  private getDecodedAccessToken(token: string): any {
    try {
      return jwt_decode(token);
    } catch (Error) {
      return null;
    }
  }

  private getUser(tokenMSAL?: string) {

    if (this.isLoggedIn()) {

      this.tokenSilentSubscription = this.getTokenSilent().subscribe((token: any) => {
        let _sesionUsuario: SesionUsuario = {};

        const tokenInfo = this.getDecodedAccessToken(token);

        if (tokenInfo.roles){

          this.forkJoinSubscription = forkJoin({
            idCiudad: this.city(),
            idUnidadResponsable: this.department(),
            idUnidadAdministrativa: this.officeLocation(),
            idPuesto: this.jobTitle(),
            idEmpleado: this.employeeId(),
            numeroFax: this.faxNumber(),
            codigoPostal: this.postalCode(),
            direccion: this.streetAddress(),
          }).subscribe((resp: ForkResponseMsal) => {

            let ciudad: string = "";
            let unidadResponsable: string = "";
            let siglasUnidadResponsable: string = "";
            let unidadAdministrativa: string = "";
            let siglasUnidadAdministrativa: string = "";
            let puesto: string = "";

            if (parseInt(resp.idCiudad)) {
              this.queryMunicipioSubscription = this._apollo
                .watchQuery({
                  query: this.GET_MUNICIPIO,
                  variables: { idMunicipio: parseInt(resp.idCiudad) },
                })
                .valueChanges.subscribe({
                  next: (respQuery: any) => {
                    if (respQuery?.data?.ctlMunicipios.length > 0) {
                      respQuery?.data?.ctlMunicipios.forEach((municipio: any) => {
                        ciudad = municipio.municipio;
                      });
                    }
                  },
                  error: (error) => {
                    console.error(error);
                  },
                });
            }

            if (parseInt(resp.idUnidadResponsable)) {
              this.queryUnidadRespSubscription = this._apollo
                .watchQuery({
                  query: this.GET_UNIDAD_RESPONSABLE, variables: { idUnidadResponsable: parseInt(resp.idUnidadResponsable) }
                })
                .valueChanges.subscribe({
                  next: (respQuery: any) => {
                    if (respQuery?.data?.ctlUnidadesResponsables.length > 0) {
                      respQuery?.data?.ctlUnidadesResponsables.forEach((unidad: any) => {
                        unidadResponsable = unidad.unidadResponsable
                        siglasUnidadResponsable = unidad.siglas
                      });
                    }
                  },
                  error: (error) => {
                    console.error(error);
                  },
                });
            }

            if (parseInt(resp.idUnidadAdministrativa)) {
              this.queryUnidadAdmSubscription = this._apollo.watchQuery({ query: this.GET_UNIDAD_ADMINISTRATIVA, variables: { idUnidadAdministrativa: parseInt(resp.idUnidadAdministrativa) } })
                .valueChanges.subscribe({
                  next: (respQuery: any) => {
                    if (respQuery?.data?.ctlUnidadesAdministrativas.length > 0) {
                      respQuery?.data?.ctlUnidadesAdministrativas.forEach((unidad: any) => {
                        unidadAdministrativa = unidad.unidadAdministrativa
                        siglasUnidadAdministrativa = unidad.siglas
                      });
                    }
                  },
                  error: (error) => {
                    console.error(error);
                  },
                });
            }

            if (parseInt(resp.idPuesto)) {
              this.queryPuestoSubscription = this._apollo.watchQuery({ query: this.GET_PUESTO, variables: { idPuesto: parseInt(resp.idPuesto) } })
                .valueChanges.subscribe({
                  next: (respQuery: any) => {
                    if (respQuery?.data?.ctlPuestos.length > 0) {
                      respQuery?.data?.ctlPuestos.forEach((puesto: any) => {
                        puesto = puesto.puesto
                      });
                    }

                  },
                  error: (error) => {
                    console.error(error);
                  },
                });
            }

            setTimeout(() => {
              _sesionUsuario = {
                isLoggedIn: true,
                name: tokenInfo.name,
                email: tokenInfo.unique_name,
                roles: JSON.stringify(tokenInfo.roles),
                tokenMSAL: tokenMSAL,
                idCiudad: parseInt(resp.idCiudad),
                ciudad: ciudad ,
                idUnidadResponsable: parseInt(resp.idUnidadResponsable),
                unidadResponsable: unidadResponsable,
                siglasUnidadResponsable: siglasUnidadResponsable,
                idUnidadAdministrativa: parseInt(resp.idUnidadAdministrativa),
                unidadAdministrativa: unidadAdministrativa,
                siglasUnidadAdministrativa: siglasUnidadAdministrativa,
                idPuesto: parseInt(resp.idPuesto),
                puesto: puesto,
                idEmpleado: parseInt(resp.idEmpleado),
                numeroFax: resp.numeroFax,
                codigoPostal: parseInt(resp.codigoPostal),
                direccion: resp.direccion
              };

            }, 1000);
          });

          setTimeout(() => {
            this.selectorLoadingSubscription = this._store.select(selectorLoading).subscribe((select) => {
                if (select) {
                  localStorage.clear();

                  this._router.navigate(['admin/registros']);

                  this._notificationService.success(
                    'Inicio de sesión satisfactorio',
                    'Autenticación'
                  );
                }
                this._store.dispatch(
                  loginSuccess({ sesionUsuario: _sesionUsuario })
                );

            });
          }, 1500);

        } else {
          this._notificationService.warning(
            'Sin permiso para acceder a la aplicación',
            'Autenticación'
          );
          this._router.navigate(['admin/login']);
        }
      });
    }
  }

  public logout() {
    this._authService.logout();
  }

  public photo(): Observable<Blob> {

    let myHeaders = new HttpHeaders();
    myHeaders.append('Content-Type', 'image/*');

    return this._http
      .get(`${_me}/photo/$value`, {
        responseType: 'blob',
      })
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }


  //#region meAzure

  private city(): Observable<string> {
    return this._http.get<MsalResponse>(`${_me}/city`)
      .pipe(take(1), map((response) => response.value));
  }

  private department(): Observable<string> {
    return this._http.get<MsalResponse>(`${_me}/department`)
      .pipe(map((response) => response.value));
  }

  private officeLocation(): Observable<string> {
    return this._http.get<MsalResponse>(`${_me}/officeLocation`)
      .pipe(map((response) => response.value));
  }

  private jobTitle(): Observable<string> {
    return this._http.get<MsalResponse>(`${_me}/jobTitle`)
      .pipe(map((response) => response.value));
  }

  private employeeId(): Observable<string> {
    return this._http.get<MsalResponse>(`${_me}/employeeId`)
      .pipe(map((response) => response.value));
  }

  private faxNumber(): Observable<string> {
    return this._http.get<MsalResponse>(`${_me}/faxnumber`)
      .pipe(map((response) => response.value));
  }

  private postalCode(): Observable<string> {
    return this._http.get<MsalResponse>(`${_me}/postalCode`)
      .pipe(map((response) => response.value));
  }

  private streetAddress(): Observable<string> {
    return this._http.get<MsalResponse>(`${_me}/streetAddress`)
      .pipe(map((response) => response.value));
  }

  //#endregion

  ngOnDestroy(): void {
    this.loginSubscription.unsubscribe();
    this.tokenSilentSubscription.unsubscribe();

    this.forkJoinSubscription.unsubscribe()

    this.selectorLoadingSubscription.unsubscribe();

    this.queryMunicipioSubscription.unsubscribe();
    this.queryUnidadRespSubscription.unsubscribe();
    this.queryUnidadAdmSubscription.unsubscribe();
    this.queryPuestoSubscription.unsubscribe();
  }
}


















